import {
  AlertCircle,
  Camera,
  Check,
  LoaderCircle,
  Lock,
  MapPin,
  ScanLine,
  WifiOff,
  X,
} from "lucide-react";
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { ApiError } from "@/lib/api";
import { matchCandadoPorCodigo } from "@/lib/barcode";
import { type Candado, useCandados } from "@/lib/candados";
import { cn } from "@/lib/cn";
import { type LotoDetail, useColocarBloqueo, useSubirFoto } from "@/lib/lotos";
import { useEstadoConexion } from "@/lib/online-status";
import { type Punto, usePuntos } from "@/lib/puntos";
import { BarcodeScanner } from "./BarcodeScanner";

const MAX_FOTOS = 2;

// OpcionBoton es un botón de selección grande (uso con guantes en exteriores): título +
// subtítulo y un tick cuando está elegido. Se usa para elegir punto y candado.
function OpcionBoton({
  titulo,
  subtitulo,
  seleccionado,
  onClick,
}: {
  titulo: string;
  subtitulo?: string;
  seleccionado: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={seleccionado}
      className={cn(
        "flex min-h-14 w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
        seleccionado
          ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500/30"
          : "border-slate-300 bg-white hover:bg-slate-50",
      )}
    >
      <span className="flex flex-col">
        <span className="font-semibold text-base text-slate-900">{titulo}</span>
        {subtitulo ? <span className="text-slate-500 text-sm">{subtitulo}</span> : null}
      </span>
      {seleccionado ? (
        <Check className="h-6 w-6 shrink-0 text-primary-600" aria-hidden="true" />
      ) : null}
    </button>
  );
}

/**
 * ColocarWizard es el asistente a pantalla completa para colgar un candado en un LOTO,
 * pensado para el terreno (móvil, targets grandes, guantes): (1) elegir UNO O VARIOS puntos
 * de aislamiento del equipo (#244: un candado puede cubrir varios; el paso exige al menos
 * uno y ofrece «Seleccionar todos»), (2) elegir un candado libre —a mano o escaneando su
 * QR/código—, (3) capturar hasta 2 fotos de verificación y (4) confirmar. Al confirmar coloca el bloqueo
 * y sube las fotos en secuencia; un fallo de foto NO revierte el candado (ya está colocado) y
 * solo avisa. El 409 CANDADO_YA_ACTIVO vuelve al paso del candado con el aviso inline.
 *
 * Offline (#54): la colocación se encola y las fotos se guardan como adjuntos que el runner sube
 * al reconectar; el wizard lo indica y cierra sin marcar fallo (subir sin red no es un error).
 */
export function ColocarWizard({
  loto,
  open,
  onClose,
}: {
  loto: LotoDetail;
  open: boolean;
  onClose: () => void;
}) {
  const obraId = useObraId();
  const puntos = usePuntos(obraId);
  const candados = useCandados(obraId);
  const colocar = useColocarBloqueo(obraId);
  const subirFoto = useSubirFoto(obraId);
  const estadoConexion = useEstadoConexion();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [paso, setPaso] = useState(1);
  // idPuntos: selección MÚLTIPLE de puntos (#244). El orden es el de selección, que es el que
  // viaja en el payload y el que se muestra en el resumen.
  const [idPuntos, setIdPuntos] = useState<number[]>([]);
  const [idCandado, setIdCandado] = useState<number | null>(null);
  const [fotos, setFotos] = useState<File[]>([]);
  const [candadoError, setCandadoError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fotoWarning, setFotoWarning] = useState<string | null>(null);
  const [escaneando, setEscaneando] = useState(false);
  // hecho evita colocar un segundo candado si el bloqueo ya se creó y solo falló alguna foto.
  const [hecho, setHecho] = useState(false);

  // Puntos del equipo del LOTO, activos: los únicos donde se puede colocar un candado.
  const puntosEquipo = useMemo(
    () => (puntos.data ?? []).filter((p: Punto) => p.id_equipo === loto.id_equipo && p.activo),
    [puntos.data, loto.id_equipo],
  );
  // Candados libres y activos: los disponibles para colgar.
  const candadosLibres = useMemo(
    () => (candados.data ?? []).filter((c: Candado) => c.estado === "LIBRE" && c.activo),
    [candados.data],
  );

  const puntosSel = useMemo(
    () => idPuntos.map((id) => puntosEquipo.find((p) => p.id === id)).filter((p) => p != null),
    [idPuntos, puntosEquipo],
  );
  const candadoSel = candadosLibres.find((c) => c.id === idCandado) ?? null;
  const todosSeleccionados = puntosEquipo.length > 0 && idPuntos.length === puntosEquipo.length;

  // El aviso de conectividad del paso de fotos solo habla cuando la falta de red está CONFIRMADA
  // (#339 D1): durante la ventana «sin confirmar» del arranque (#237) el asistente se calla, como
  // ya hacía `lib/filtros.ts::avisoSinConexion` desde #247. Es una superficie que el usuario abre a
  // propósito, con la acción en marcha: un aviso que aparece y se va en menos de un segundo es
  // ruido, no información. Callarse NO es afirmar que hay red: el camino de la colocación y el de
  // la foto los sigue decidiendo `getOnline()` en `lib/lotos.ts`, fail-closed, y sin confirmar
  // ambas se encolan exactamente igual que offline.
  const sinConexionConfirmada = estadoConexion === "offline";

  // alternarPunto conmuta un punto de la selección (marcar/desmarcar), conservando el orden
  // en el que se fueron eligiendo.
  function alternarPunto(id: number) {
    setIdPuntos((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  // Previsualizaciones de las fotos elegidas; se revocan al cambiar para no filtrar memoria.
  const previews = useMemo(() => fotos.map((f) => URL.createObjectURL(f)), [fotos]);
  useEffect(() => {
    return () => {
      for (const url of previews) {
        URL.revokeObjectURL(url);
      }
    };
  }, [previews]);

  function reiniciar() {
    setPaso(1);
    setIdPuntos([]);
    setIdCandado(null);
    setFotos([]);
    setCandadoError(null);
    setSubmitError(null);
    setFotoWarning(null);
    setEscaneando(false);
    setHecho(false);
  }

  // onCodigoEscaneado empareja el código leído por la cámara con un candado LIBRE y lo selecciona.
  // Si no coincide con ninguno disponible, avisa inline (código de otro candado, ya en uso o ajeno al
  // catálogo) sin cambiar la selección. El escaneo es solo una ayuda de captura: la validación real
  // (candado libre, no doble colocación) sigue siendo del servidor al colocar.
  function onCodigoEscaneado(codigo: string) {
    setEscaneando(false);
    const candado = matchCandadoPorCodigo(candadosLibres, codigo);
    if (!candado) {
      setCandadoError(`No hay ningún candado libre con el código «${codigo.trim()}».`);
      return;
    }
    setIdCandado(candado.id);
    setCandadoError(null);
  }

  function cerrar() {
    reiniciar();
    onClose();
  }

  function onElegirFoto(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file && fotos.length < MAX_FOTOS) {
      setFotos((prev) => [...prev, file]);
    }
    // Permite volver a elegir el mismo archivo (el navegador no dispara change si no cambia).
    e.target.value = "";
  }

  function quitarFoto(idx: number) {
    setFotos((prev) => prev.filter((_, i) => i !== idx));
  }

  async function confirmar() {
    if (idPuntos.length === 0 || idCandado == null || hecho) {
      return;
    }
    setSubmitError(null);
    setFotoWarning(null);
    try {
      const bloqueo = await colocar.mutateAsync({
        idLoto: loto.id,
        input: { id_puntos: idPuntos, id_candado: idCandado },
      });
      // Subida secuencial de fotos; un fallo NO revierte el candado (ya colocado). Sin conexión, la
      // subida se encola (no lanza), así que `fallos` queda a 0 y el wizard cierra con normalidad.
      let fallos = 0;
      for (let i = 0; i < fotos.length; i += 1) {
        try {
          await subirFoto.mutateAsync({
            idBloqueo: bloqueo.id,
            idLoto: loto.id,
            file: fotos[i],
            orden: i + 1,
          });
        } catch {
          fallos += 1;
        }
      }
      if (fallos > 0) {
        setHecho(true);
        setFotoWarning(
          `El candado se colocó, pero ${fallos} foto(s) no se pudieron subir. Puedes reintentarlo desde la ficha.`,
        );
        return;
      }
      cerrar();
    } catch (err) {
      if (err instanceof ApiError && err.code === "CANDADO_YA_ACTIVO") {
        setCandadoError("Ese candado ya está colocado en otro bloqueo activo.");
        setIdCandado(null);
        setPaso(2);
        return;
      }
      if (err instanceof ApiError && err.code === "LOTO_CERRADO") {
        setSubmitError("El LOTO está cerrado: no admite nuevos candados.");
        return;
      }
      setSubmitError(
        err instanceof ApiError
          ? err.message
          : "No se pudo colocar el candado. Inténtalo de nuevo.",
      );
    }
  }

  const enviando = colocar.isPending || subirFoto.isPending;

  return (
    <>
      <Modal open={open} onClose={cerrar} title="Colocar candado" className="sm:max-w-lg">
        {/* Indicador de paso */}
        <p className="font-medium text-slate-500 text-sm">Paso {Math.min(paso, 4)} de 4</p>

        {/* Paso 1: punto de aislamiento */}
        {paso === 1 ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary-600" aria-hidden="true" />
                <h3 className="font-bold text-lg text-slate-900">
                  Elige los puntos de aislamiento
                </h3>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIdPuntos(todosSeleccionados ? [] : puntosEquipo.map((p) => p.id))}
                disabled={puntosEquipo.length === 0}
              >
                {todosSeleccionados ? "Quitar todos" : "Seleccionar todos"}
              </Button>
            </div>
            <p className="text-slate-500 text-sm">
              Un mismo candado puede cubrir varios puntos. Marca todos los que aísla.
            </p>
            {puntosEquipo.length === 0 ? (
              <div className="rounded-xl border border-slate-200 border-dashed px-4 py-8 text-center text-slate-500">
                Este equipo no tiene puntos de aislamiento activos.
              </div>
            ) : (
              <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto">
                {puntosEquipo.map((p) => (
                  <OpcionBoton
                    key={p.id}
                    titulo={`${p.nombre} · ${p.descripcion}`}
                    subtitulo={p.nombre_fuente}
                    seleccionado={idPuntos.includes(p.id)}
                    onClick={() => alternarPunto(p.id)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : null}

        {/* Paso 2: candado */}
        {paso === 2 ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-red-600" aria-hidden="true" />
                <h3 className="font-bold text-lg text-slate-900">Elige el candado</h3>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setEscaneando(true)}
                disabled={candadosLibres.length === 0}
              >
                <ScanLine className="h-5 w-5" aria-hidden="true" />
                Escanear
              </Button>
            </div>
            {candadoError ? (
              <Alert variant="destructive">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{candadoError}</span>
              </Alert>
            ) : null}
            {candadosLibres.length === 0 ? (
              <div className="rounded-xl border border-slate-200 border-dashed px-4 py-8 text-center text-slate-500">
                No hay candados libres disponibles.
              </div>
            ) : (
              <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto">
                {candadosLibres.map((c) => (
                  <OpcionBoton
                    key={c.id}
                    titulo={c.codigo_candado}
                    subtitulo={c.descripcion ?? undefined}
                    seleccionado={c.id === idCandado}
                    onClick={() => {
                      setIdCandado(c.id);
                      setCandadoError(null);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        ) : null}

        {/* Paso 3: fotos de verificación */}
        {paso === 3 ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-primary-600" aria-hidden="true" />
              <h3 className="font-bold text-lg text-slate-900">Fotos de verificación</h3>
            </div>
            <p className="text-slate-500 text-sm">
              Añade hasta 2 fotos de verificación del candado colocado (opcional).
            </p>
            {sinConexionConfirmada ? (
              <Alert variant="info">
                <WifiOff className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <span>Sin conexión: las fotos se guardan y se subirán al reconectar.</span>
              </Alert>
            ) : null}
            {fotos.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {previews.map((url, idx) => (
                  <div key={url} className="relative">
                    <img
                      src={url}
                      alt={`Foto de verificación ${idx + 1}`}
                      className="h-32 w-full rounded-xl border border-slate-200 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => quitarFoto(idx)}
                      aria-label={`Quitar foto ${idx + 1}`}
                      className="absolute top-1 right-1 flex h-8 w-8 items-center justify-center rounded-full bg-slate-900/60 text-white hover:bg-slate-900/80"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={onElegirFoto}
            />
            {fotos.length < MAX_FOTOS ? (
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="h-5 w-5" aria-hidden="true" />
                Añadir foto
              </Button>
            ) : null}
          </div>
        ) : null}

        {/* Paso 4: confirmación */}
        {paso === 4 ? (
          <div className="flex flex-col gap-3">
            <h3 className="font-bold text-lg text-slate-900">Confirmar colocación</h3>
            <dl className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Equipo</dt>
                <dd className="text-right font-medium text-slate-900">
                  {loto.equipo} · {loto.codigo_equipo}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">
                  {puntosSel.length === 1 ? "Punto" : `Puntos (${puntosSel.length})`}
                </dt>
                <dd className="text-right font-medium text-slate-900">
                  {puntosSel.length > 0 ? puntosSel.map((p) => p.nombre).join(", ") : "—"}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Candado</dt>
                <dd className="text-right font-medium text-slate-900">
                  {candadoSel?.codigo_candado ?? "—"}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Fotos</dt>
                <dd className="text-right font-medium text-slate-900">{fotos.length} de 2</dd>
              </div>
            </dl>
            {submitError ? (
              <Alert variant="destructive">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{submitError}</span>
              </Alert>
            ) : null}
            {fotoWarning ? (
              <Alert variant="info">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <span>{fotoWarning}</span>
              </Alert>
            ) : null}
          </div>
        ) : null}

        {/* Navegación del asistente */}
        <div className="flex justify-between gap-2">
          {paso > 1 && !hecho ? (
            <Button variant="outline" onClick={() => setPaso((p) => p - 1)} disabled={enviando}>
              Atrás
            </Button>
          ) : (
            <span />
          )}
          {hecho ? (
            <Button onClick={cerrar}>Cerrar</Button>
          ) : paso < 4 ? (
            <Button
              onClick={() => setPaso((p) => p + 1)}
              disabled={(paso === 1 && idPuntos.length === 0) || (paso === 2 && idCandado == null)}
            >
              Siguiente
            </Button>
          ) : (
            <Button onClick={() => void confirmar()} disabled={enviando}>
              {enviando ? (
                <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
              ) : (
                <Lock className="h-5 w-5" aria-hidden="true" />
              )}
              Colocar candado
            </Button>
          )}
        </div>
      </Modal>
      <BarcodeScanner
        open={escaneando}
        titulo="Escanear candado"
        onDetected={onCodigoEscaneado}
        onClose={() => setEscaneando(false)}
      />
    </>
  );
}
